import { FireBook, FireInsight } from '../models/FirestoreModels';
import { BookRepository } from '../repositories/bookRepository';
import { BookService } from './bookService';

export interface ReaderSegment {
  type: 'intro' | 'insight';
  id: string;
  title: string;
  subtitle?: string;
  contentText: string;
  bookId?: string;
  bookTitle?: string;
  bookCoverUrl?: string;
  aboutAuthor?: string;
  // Insight-specific fields
  whatToAchieve?: string;
  actionSteps?: Array<{ directive: string; explanation: string }>;
  reflectionQuestions?: string[];
  recommendationReason?: string; // Will be loaded separately from user profile
  // Source data for audio playlist building and parity with legacy behavior
  sourceInsight?: FireInsight; // present when type === 'insight'
}

export interface ReaderData {
  segments: ReaderSegment[];
  title: string;
  subtitle?: string;
  coverUrl?: string;
  book?: FireBook;
  isCollection: boolean;
}

/**
 * Service to build unified segment lists for the Reader experience
 */
export class ReaderSegmentsService {
  /**
   * Build segments for a complete book (intro + insights)
   */
  static async buildBookSegments(
    bookId: string,
    languageCode: string = 'en',
    includeIntroduction: boolean = true
  ): Promise<ReaderData> {
    console.log(`📚 Building book segments for bookId: ${bookId}, lang: ${languageCode}`);
    
    // Fetch full book data
    const result = await BookService.getBookDetail(bookId, languageCode);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to load book data');
    }
    
    const { book, insights } = result.data;
    const segments: ReaderSegment[] = [];
    
    // Add introduction segment if available and requested
    if (includeIntroduction && book.introductionMarkdown) {
      segments.push({
        type: 'intro',
        id: 'introduction',
        title: book.title,
        subtitle: book.subtitle,
        contentText: book.introductionMarkdown,
        bookId: book.id,
        bookTitle: book.title,
        bookCoverUrl: book.coverImageUrl,
        aboutAuthor: book.aboutAuthor,
      });
    }
    
    // Add insight segments
    insights.forEach((insight) => {
      segments.push({
        type: 'insight',
        id: insight.id,
        title: insight.title,
        subtitle: insight.whatToAchieve,
        contentText: insight.storyContentMarkdown,
        bookId: book.id,
        bookTitle: book.title,
        bookCoverUrl: book.coverImageUrl,
        whatToAchieve: insight.whatToAchieve,
        actionSteps: insight.actionSteps,
        reflectionQuestions: insight.reflectionQuestions,
        sourceInsight: insight,
      });
    });
    
    console.log(`✅ Built ${segments.length} segments for book: ${book.title}`);
    
    return {
      segments,
      title: book.title,
      subtitle: book.subtitle,
      coverUrl: book.coverImageUrl,
      book,
      isCollection: false,
    };
  }
  
  /**
   * Build segments for a collection of insights (no intro)
   * Loads book details for each insight to show correct source
   */
  static async buildCollectionSegments(
    insightIds: string[],
    languageCode: string = 'en'
  ): Promise<ReaderData> {
    console.log(`📚 Building collection segments for ${insightIds.length} insights`);
    
    // Fetch insights directly
    const insights = await BookRepository.getInsightsByIds(insightIds, languageCode);
    
    if (insights.length === 0) {
      throw new Error('No insights found for collection');
    }
    
    // Check if insights are from multiple books
    const uniqueBookIds = new Set(insights.map(i => i.bookId).filter(Boolean));
    const isMixedBooks = uniqueBookIds.size > 1;
    
    console.log(`📚 Collection has ${uniqueBookIds.size} book(s), mixed: ${isMixedBooks}`);
    
    const segments: ReaderSegment[] = [];
    const bookCache = new Map<string, FireBook>();
    
    // Load book details for each insight (if mixed) or once (if single book)
    if (isMixedBooks || uniqueBookIds.size === 1) {
      // Load all unique books
      for (const bookId of uniqueBookIds) {
        if (!bookId) continue;
        try {
          const bookResult = await BookService.getBookDetail(bookId, languageCode);
          if (bookResult.success && bookResult.data) {
            bookCache.set(bookId, bookResult.data.book);
            console.log('✅ Loaded book for collection:', bookResult.data.book.title);
          }
        } catch (error) {
          console.error('Failed to load book for insight:', bookId, error);
        }
      }
    }
    
    // Add insight segments with book data
    insights.forEach((insight) => {
      const insightBook = insight.bookId ? bookCache.get(insight.bookId) : undefined;
      
      segments.push({
        type: 'insight',
        id: insight.id,
        title: insight.title,
        subtitle: insight.whatToAchieve,
        contentText: insight.storyContentMarkdown,
        bookId: insight.bookId,
        bookTitle: insightBook?.title,
        bookCoverUrl: insightBook?.coverImageUrl,
        aboutAuthor: insightBook?.aboutAuthor,
        whatToAchieve: insight.whatToAchieve,
        actionSteps: insight.actionSteps,
        reflectionQuestions: insight.reflectionQuestions,
        sourceInsight: insight,
      });
    });
    
    // Determine title based on collection
    const title = isMixedBooks 
      ? 'Curated Insights'
      : bookCache.values().next().value?.title || 'Insights';
    
    console.log(`✅ Built ${segments.length} segments from ${bookCache.size} book(s)`);
    
    return {
      segments,
      title,
      subtitle: isMixedBooks ? `From ${bookCache.size} books` : undefined,
      isCollection: true,
    };
  }
  
  /**
   * Build segments from existing data (for migration/backwards compatibility)
   * For mixed insights from multiple books, loads each book individually
   */
  static async buildSegmentsFromInsights(
    insights: FireInsight[],
    book?: FireBook,
    includeIntroduction: boolean = false
  ): Promise<ReaderData> {
    const segments: ReaderSegment[] = [];
    
    // Check if insights are from multiple books
    const uniqueBookIds = new Set(insights.map(i => i.bookId).filter(Boolean));
    const isMixedBooks = uniqueBookIds.size > 1;
    
    console.log(`📚 Building segments for ${insights.length} insights from ${uniqueBookIds.size} book(s)`);
    
    // If single book provided or all insights from same book, use simpler logic
    if (book || (!isMixedBooks && uniqueBookIds.size === 1)) {
      let bookData = book;
      
      // Load book if not provided
      if (!bookData && insights.length > 0 && insights[0].bookId) {
        try {
          console.log('📚 Loading book:', insights[0].bookId);
          const bookResult = await BookService.getBookDetail(insights[0].bookId, 'en');
          if (bookResult.success && bookResult.data) {
            bookData = bookResult.data.book;
            console.log('✅ Loaded book:', bookData.title);
          }
        } catch (error) {
          console.error('Failed to load book:', error);
        }
      }
      
      // Add introduction if requested
      if (includeIntroduction && bookData?.introductionMarkdown) {
        segments.push({
          type: 'intro',
          id: 'introduction',
          title: bookData.title,
          subtitle: bookData.subtitle,
          contentText: bookData.introductionMarkdown,
          bookId: bookData.id,
          bookTitle: bookData.title,
          bookCoverUrl: bookData.coverImageUrl,
          aboutAuthor: bookData.aboutAuthor,
        });
      }
      
      // Add insights with book data
      insights.forEach((insight) => {
        segments.push({
          type: 'insight',
          id: insight.id,
          title: insight.title,
          subtitle: insight.whatToAchieve,
          contentText: insight.storyContentMarkdown,
          bookId: insight.bookId || bookData?.id,
          bookTitle: bookData?.title,
          bookCoverUrl: bookData?.coverImageUrl,
          aboutAuthor: bookData?.aboutAuthor,
          whatToAchieve: insight.whatToAchieve,
          actionSteps: insight.actionSteps,
          reflectionQuestions: insight.reflectionQuestions,
          sourceInsight: insight,
        });
      });
      
      return {
        segments,
        book: bookData,
        title: bookData?.title || 'Insights',
        subtitle: bookData?.subtitle,
        coverUrl: bookData?.coverImageUrl,
        isCollection: false,
      };
    }
    
    // MIXED BOOKS: Load each book individually for each insight
    console.log('🎨 Mixed insights from multiple books - loading each book');
    const bookCache = new Map<string, FireBook>();
    
    for (const insight of insights) {
      if (!insight.bookId) {
        console.warn('⚠️ Insight missing bookId:', insight.id);
        continue;
      }
      
      // Check cache first
      let insightBook = bookCache.get(insight.bookId);
      
      // Load if not cached
      if (!insightBook) {
        try {
          const bookResult = await BookService.getBookDetail(insight.bookId, 'en');
          if (bookResult.success && bookResult.data) {
            insightBook = bookResult.data.book;
            bookCache.set(insight.bookId, insightBook);
            console.log('✅ Loaded book for insight:', insightBook.title);
          }
        } catch (error) {
          console.error('Failed to load book for insight:', insight.bookId, error);
        }
      }
      
      // Add segment with book-specific data
      segments.push({
        type: 'insight',
        id: insight.id,
        title: insight.title,
        subtitle: insight.whatToAchieve,
        contentText: insight.storyContentMarkdown,
        bookId: insight.bookId,
        bookTitle: insightBook?.title,
        bookCoverUrl: insightBook?.coverImageUrl,
        aboutAuthor: insightBook?.aboutAuthor,
        whatToAchieve: insight.whatToAchieve,
        actionSteps: insight.actionSteps,
        reflectionQuestions: insight.reflectionQuestions,
        sourceInsight: insight,
      });
    }
    
    console.log(`✅ Built ${segments.length} segments from ${bookCache.size} books`);
    
    return {
      segments,
      book: undefined, // No single book for mixed collection
      title: 'Curated Insights',
      subtitle: `From ${bookCache.size} books`,
      coverUrl: undefined,
      isCollection: true,
    };
  }
}

